import { useRef, useEffect, useMemo } from "react";
import { ReactGanttRef } from "@dhx/react-gantt";
import {
  ProjectForGantt,
  transformProjectsToGanttTasks,
} from "../utils/ganttTransformers";
import { ZoomLevel } from "../components/ProjectsGanttView";
import { formatDate } from "../../../shared/utils";

export const useGantt = (
  projects: ProjectForGantt[],
  zoomLevel: ZoomLevel,
  onProjectClick?: (projectId: number, projectName: string) => void,
) => {
  const ganttRef = useRef<ReactGanttRef>(null);

  const tasks = useMemo(
    () => transformProjectsToGanttTasks(projects),
    [projects],
  );

  const expandAll = () => {
    const gantt = ganttRef.current?.instance;
    if (!gantt) return;

    gantt.eachTask((task: any) => {
      if (task.type === "project") gantt.open(task.id);
    });
  };

  const collapseAll = () => {
    const gantt = ganttRef.current?.instance;
    if (!gantt) return;

    gantt.eachTask((task: any) => {
      if (task.type === "project") gantt.close(task.id);
    });
  };

  const getScalesForZoom = (zoom: ZoomLevel) => {
    switch (zoom) {
      case "day":
        return [
          { unit: "month", step: 1, format: "%M %Y" },
          { unit: "day", step: 1, format: "%d %M" },
        ];
      case "week":
        return [
          { unit: "month", step: 1, format: "%M %Y" },
          { unit: "week", step: 1, format: "Wk #%W" },
        ];
      case "month":
        return [
          { unit: "year", step: 1, format: "%Y" },
          { unit: "month", step: 1, format: "%M %Y" },
        ];
      case "year":
        return [{ unit: "year", step: 1, format: "%Y" }];
    }
  };

  useEffect(() => {
    const gantt = ganttRef.current?.instance;
    if (!gantt) return;

    gantt.config.scales = getScalesForZoom(zoomLevel) as any;
    gantt.render();
  }, [zoomLevel]);

  useEffect(() => {
    const gantt = ganttRef.current?.instance;
    if (!gantt) return;

    const handleGridClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const iconEl = target.closest(".gantt-view-icon");
      if (iconEl) {
        const projectIdAttr = iconEl.getAttribute("data-project-id");
        if (projectIdAttr?.startsWith("project_")) {
          const projectId = Number(projectIdAttr.replace("project_", ""));
          const project = projects.find((p) => p.id === projectId);
          if (project && onProjectClick)
            onProjectClick(projectId, project.title);
        }
      }
    };

    const gridElement = gantt.$grid;
    gridElement?.addEventListener("click", handleGridClick);
    return () => {
      gridElement?.removeEventListener("click", handleGridClick);
    };
  }, [projects, onProjectClick]);

  useEffect(() => {
      const gantt = ganttRef.current?.instance;
      if (!gantt) return;
    
      gantt.templates.grid_folder = (task: any) => {
        if (task.type === "project") {
          return "<div class='gantt_tree_icon gantt_icon_phase'></div>";
        }
        return "<div class='gantt_tree_icon gantt_icon_activity'></div>";
      };
  
      gantt.templates.grid_file = (task: any) => {
        if (task.type === "project") {
          return "<div class='gantt_tree_icon gantt_icon_phase'></div>";
        }
        if (task.type === "milestone") {
          return "<div class='gantt_tree_icon gantt_icon_milestone'></div>";
        }
        return "<div class='gantt_tree_icon gantt_icon_activity'></div>";
      };

      gantt.templates.date_grid = (date: Date, task: any) => {
        if (task && gantt.isUnscheduledTask(task)) {
          return gantt.templates.task_unscheduled_time(task) || ""
        }
        if(!date || !date.getFullYear) return "";
        return formatDate(date);
      };
      
      gantt.templates.tooltip_text = (start: Date, end: Date, task: any) => {
        return `
          ${task.type === "project" ? "Project" : "Phase"}: ${task.text}<br/>
          Start Date: ${start ? formatDate(start) : "-"}<br/>
          End Date: ${end ? formatDate(end) : "-"}<br/>
        `;
      };
  
      gantt.render();
    }, [tasks]);

  return { ganttRef, tasks, expandAll, collapseAll };
};
